// src/pages/LedgerAdminPage.jsx
import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

export default function LedgerAdminPage() {
  const [data, setData] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch("/api/ledger?firma=LEMON", {
          method: "GET",
          credentials: "include",
        });

        if (!res.ok) throw new Error("Chyba při načítání ledgeru");

        const json = await res.json();
        setData(json?.result || []);
      } catch (err) {
        console.error(err);
        setError("Nepodařilo se načíst účetní knihu.");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const toggleExpand = (pharmacyCode) => {
    setExpanded(expanded === pharmacyCode ? null : pharmacyCode);
  };

  const formatCurrency = (value) => {
    return value
      .toLocaleString("cs-CZ", { style: "currency", currency: "CZK" })
      .replace(/\. /g, ".");
  };

  if (loading) return <div className="p-4 text-gray-600">Načítám data...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-6">Účetní kniha – přehled poboček</h1>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full border-collapse">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-3 px-4 text-left font-medium text-gray-700">Pobočka</th>
              <th className="py-3 px-4 text-right font-medium text-gray-700">Celkové saldo</th>
              <th className="py-3 px-4 text-center font-medium text-gray-700">Detail</th>
            </tr>
          </thead>

          <tbody>
            {data.map((row) => (
              <React.Fragment key={row.pharmacyCode}>
                <tr className="border-t hover:bg-gray-50">
                  <td className="py-3 px-4 font-semibold">{row.pharmacyCode}</td>

                  <td
                    className={`py-3 px-4 text-right font-medium ${
                      row.totalSaldo > 0
                        ? "text-green-600"
                        : row.totalSaldo < 0
                        ? "text-red-600"
                        : "text-gray-500"
                    }`}
                  >
                    {formatCurrency(row.totalSaldo)}
                  </td>

                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={() => toggleExpand(row.pharmacyCode)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {expanded === row.pharmacyCode ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      )}
                    </button>
                  </td>
                </tr>

                {expanded === row.pharmacyCode && (
                  <tr className="bg-gray-50 border-t">
                    <td colSpan="3" className="p-4">
                      <h3 className="font-semibold text-gray-700 mb-2">
                        Vztahy pobočky {row.pharmacyCode}:
                      </h3>

                      {row.relations.length > 0 ? (
                        <table className="min-w-full border text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="py-2 px-3 text-left">Cílová pobočka</th>
                              <th className="py-2 px-3 text-right">Saldo</th>
                            </tr>
                          </thead>

                          <tbody>
                            {row.relations.map((r) => (
                              <tr
                                key={r.toPharmacy || r.targetPharmacy}
                                className="border-t"
                              >
                                <td className="py-2 px-3">
                                  {r.toPharmacy || r.targetPharmacy}
                                </td>

                                <td
                                  className={`py-2 px-3 text-right ${
                                    r.saldo > 0
                                      ? "text-green-600"
                                      : r.saldo < 0
                                      ? "text-red-600"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {formatCurrency(r.saldo)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="text-gray-500 text-sm italic">
                          Žádné vztahy
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
